import React, { useCallback, useState } from 'react';
import { Box, Dialog } from '@mui/material';
import AuthUtil from '../../../redux/AuthUtil';
import { TICKET_OUT } from '../../global/Server/endpoints';
import { TOKENS } from '../theme';

// Shared full-screen viewer for a Ticket Out photo (spec §9). Photos are
// fetched on demand from /ticketOut/previewPhoto by ticketId rather than
// shipped inline with the session report, keeping the report payload small.
//
// Returns { view(ticketId), node } — render `node` once in the component and
// call `view(ticketId)` to open the viewer. The screen is already gated to
// managers/owners, and previewPhoto is @Secured, so no extra role check here.
export default function useTicketPhotoViewer() {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  const view = useCallback((ticketId) => {
    if (!ticketId) return;
    setOpen(true);
    setLoading(true);
    setSrc(null);
    AuthUtil.postRequest(TICKET_OUT.PREVIEW_PHOTO, { ticketId })
      .then((res) => {
        if (res.data && res.data.success && res.data.previewedFile) {
          const blob = new Blob([new Uint8Array(res.data.previewedFile)], {
            type: 'image/jpeg',
          });
          setSrc(URL.createObjectURL(blob));
        }
      })
      .catch(() => {
        /* leave src null → "Photo not available" */
      })
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const node = (
    <Dialog open={open} onClose={close} maxWidth="md">
      {loading ? (
        <Box sx={{ p: 4, color: TOKENS.slate500, fontSize: 14 }}>Loading…</Box>
      ) : src ? (
        <img
          src={src}
          alt="Ticket"
          style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
        />
      ) : (
        <Box sx={{ p: 4, color: TOKENS.slate500, fontSize: 14 }}>
          Photo not available.
        </Box>
      )}
    </Dialog>
  );

  return { view, node };
}
